// R5.2 / R5.7 — LAYER 2: entry facets (spec §4.2), rendered PER ENTRY inside the entry card.
//
// Per entry: Responsibility / Completion / Continuity, each as a LEVEL LABEL from its own registered copy id —
// "Levels render as labels, never numerals" (§4.2) — under the uniform band treatment (§1.4). `CS-FACET-FRAMING`
// renders ONCE above the first facet display (the entries section renders it, `EntryCards.tsx`) and is MANDATORY:
// §3.0's framing is what stops a level ladder from reading as a quality ladder ("No level is better than another").
// Observership entries carry `CS-OBSERVERSHIP`, which says what an observership IS rather than what it lacks (§3.5 S6).
//
// EACH LEVEL SHOWS ITS "WHY": the quoted span(s) that established it, resolved through the standard
// `evidence_fact_ids → fact → EvidenceSpan` lookup (§4.2, closing R5-8P-02) — the payload carries
// `evidence_fact_ids`, never `RuleTrace` objects, because the whole facet `trace` is deny-listed (R5-9P-01).
//
// ORDER IS FIXED (T-ORDER-1): the three facets always Responsibility → Completion → Continuity. Nothing here
// sorts, filters, or promotes by level. Entry order is the entries section's (the payload's document order).
//
// R5.7 (owner direction 2026-08-22): the standalone facet STRIP is gone — the legacy read grouped everything per
// entry, and so does this surface now. What was a section of entry cards is a fragment rendered inside each card,
// in the legacy's compact one-row form: three label/chip pairs, each with its own source reveal.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import type { CopyStringId } from './copy';
// R5.8 / 2026-08-23 — a not-expected facet at raw L1 renders NOTHING. `facetNotExpectedCopyId` still IDENTIFIES the
// case (and still names `CS-FACET-NA`, still registered); this surface now treats a non-null answer as "omit the row".
import { FACET_L1_LEVEL, facetNotExpectedCopyId, facetRenders } from '../../../workers/src/lib/entryTypeProfiles';
// C2b FAIL-CLOSED (R5.7b fix): an L1 level is an omission claim and renders ONLY behind a valid per-entry scope.
import { scopeValid } from './scope';
import { BandChip, Copy, EvidenceReveal } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { BODY_STYLE, EVIDENCE_QUOTE_STYLE, EVIDENCE_SUMMARY_STYLE, FACET_ROW_STYLE, MUTED_STYLE } from './styles';

type Entry = RubricReadPublicV1['entries'][number];

/** The three facets, in their fixed order, with the registered NAME id for each. Never re-ordered, never sorted. */
const FACETS: Array<{ key: 'responsibility' | 'completion' | 'continuity'; nameId: CopyStringId }> = [
  { key: 'responsibility', nameId: 'CS-FACET-RESPONSIBILITY' },
  { key: 'completion', nameId: 'CS-FACET-COMPLETION' },
  { key: 'continuity', nameId: 'CS-FACET-CONTINUITY' },
];

/** The DOM id of an entry's card — the target of Layer 3's qualifying-entry links. A SYSTEM id (§1.5 rule 2). */
export function entryDomId(entry_id: string): string {
  return `rubric-entry-${entry_id}`;
}

/** One entry's three facet levels, with their evidence. */
export function EntryFacets({ entry, evidence }: { entry: Entry; evidence: EvidenceIndex }) {
  const rows = facetRows(entry, evidence);
  return (
    <>
      {entry.observership_copy_string_id && (
        <p style={BODY_STYLE} data-testid="rubric-observership">
          <Copy id={entry.observership_copy_string_id} />
        </p>
      )}
      {rows.length > 0 && (
        <dl style={FACET_ROW_STYLE} data-testid="rubric-entry-facets">
          {rows}
        </dl>
      )}
    </>
  );
}

/**
 * The rows an entry actually has, or an empty array. Split out from the render so the `<dl>` itself can be
 * omitted when nothing survives: an empty definition list is a labelled shell announcing a list with no items.
 */
function facetRows(entry: Entry, evidence: EvidenceIndex) {
  return FACETS.flatMap(({ key, nameId }) => {
      const facet = entry[key];
      // C2b FAIL-CLOSED — an L1 decision (including a CS-FACET-NA gloss of one) is an OMISSION claim; without a
      // present, valid, non-empty per-entry scope the claim is not made: the row is omitted, never rendered bare.
      const isL1 = facet.level === FACET_L1_LEVEL[key];
      if (isL1 && !scopeValid(facet.search_scope)) return [];
      // 2026-08-23 — AN `unknown` EXPECTATION AT L1 RENDERS NOTHING AT ALL. We could not place the entry, so
      // we have no convention to measure it against: "Role not stated" would assert an omission against a
      // convention nobody established, and `CS-FACET-NA` would assert that convention positively does not
      // expect it. Both are claims we did not earn. The row is omitted — the same fail-closed shape as the
      // C2b scope check directly above, for the same reason: a claim without its ground is not made.
      if (!facetRenders(entry.expectations, key, facet.level)) return [];
      // 2026-08-23 (OWNER VERDICT ON A LIVE READ): "if not expected, render NOTHING — collapse the facet
      // block entirely." `CS-FACET-NA` ("Not expected for this kind of entry — a CV conventionally does not
      // state this here.") printed THREE TIMES on a membership entry, which is three sentences about what a
      // CV does not conventionally say, and none about the applicant's own document. A convention that does
      // not expect the facet has nothing to report, so nothing is reported: the row is omitted, the same
      // fail-closed shape as the two checks above.
      //
      // THIS CHANGES `facetNotExpectedCopyId`'s CONTRACT AT THE SURFACE, NOT IN THE ENGINE. That function
      // lives in `entryTypeProfiles.ts` and is unedited: it still answers "is this a not-expected L1, and
      // what would it be labelled". The suppression is HERE, so a non-null answer now means "omit" rather
      // than "render this label". `CS-FACET-NA` stays REGISTERED (Appendix A.5-P.13) and inside every ban
      // sweep — de-registering it would change the codebook's id set, and nothing about the id was wrong.
      if (facetNotExpectedCopyId(entry.expectations, key, facet.level) !== null) return [];
      return (
        <div key={key} style={{ margin: 0, minWidth: 0 }} data-facet={key}>
          <dt style={{ ...MUTED_STYLE, margin: 0 }}>
            <Copy id={nameId} />
          </dt>
          <dd style={{ margin: 'var(--space-1) 0 0' }}>
            <BandChip id={facet.copy_string_id as CopyStringId} />
            {/* C2b — an L1 level is an OMISSION claim and carries a per-entry closed-world `search_scope` instead of
                spans (R5.8 verify-round-2 launch blocker). Its "why" is the scope disclosure, worded for the scope
                it actually has (`CS-OMISSION-SCOPE-ENTRY`, never "all 1 entries"); an L2+ level's "why" is its
                quoted spans. The raw
                `scope_description` is audit-only and is never rendered. */}
            {isL1
              ? <FacetScopeReveal />
              : <EvidenceReveal evidence={resolveEvidence(evidence, facet.evidence_fact_ids)} />}
          </dd>
        </div>
      );
  });
}

/**
 * The L1 "why": the same `<details>` shape as `EvidenceReveal` (keyboard-operable, no ARIA of ours), opened by the
 * same registered `CS-SHOW-SOURCE` label, disclosing the registered per-entry scope sentence rather than a quote.
 */
function FacetScopeReveal() {
  return (
    <details className="rubric-evidence" data-testid="rubric-facet-scope">
      <summary style={EVIDENCE_SUMMARY_STYLE}><Copy id="CS-SHOW-SOURCE" /></summary>
      <p style={{ ...EVIDENCE_QUOTE_STYLE, ...MUTED_STYLE, margin: 'var(--space-2) 0 0' }}>
        <Copy id="CS-OMISSION-SCOPE-ENTRY" />
      </p>
    </details>
  );
}
